import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Boxes, Search } from "lucide-react";
import { formatCLP } from "@/lib/tienda";

/** Inventario: el admin ve todo valorizado (costo/venta/margen y stock por
 *  técnico); el técnico ve solo su inventario asignado. */
export default function TiendaInventario() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/api/tienda/inventario").then((r) => setData(r.data)).catch(() => {});
  }, [api]);

  if (!data) return <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center text-gray-400">Cargando…</div>;

  const items = (data.items || []).filter((i) =>
    !q || `${i.nombre} ${i.sku} ${i.codigo_barras}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Boxes className="h-5 w-5 text-[#E0653A]" />
            <span className="text-sm font-semibold text-[#384091]">
              {data.admin ? "Inventario general" : "Mi inventario"}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-5 space-y-4">
        {/* valorización */}
        <div className={`grid ${data.admin ? "grid-cols-3" : "grid-cols-1"} gap-3`}>
          {data.admin ? (
            <>
              <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center">
                <p className="text-xl font-bold text-[#384091]">{formatCLP(data.valorizacion?.costo)}</p>
                <p className="text-xs text-gray-500">Valorizado a costo</p></CardContent></Card>
              <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center">
                <p className="text-xl font-bold text-[#0FB4B7]">{formatCLP(data.valorizacion?.venta)}</p>
                <p className="text-xs text-gray-500">Valorizado a venta</p></CardContent></Card>
              <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center">
                <p className="text-xl font-bold text-emerald-600">{formatCLP(data.valorizacion?.margen)}</p>
                <p className="text-xs text-gray-500">Margen potencial</p></CardContent></Card>
            </>
          ) : (
            <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center">
              <p className="text-xl font-bold text-[#384091]">{formatCLP(data.valorizacion?.costo)}</p>
              <p className="text-xs text-gray-500">Valor de mi inventario (a costo)</p></CardContent></Card>
          )}
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, SKU o código de barras…"
            className="pl-9 h-10 bg-white no-uppercase" />
        </div>

        <div className="space-y-2">
          {items.length === 0 && (
            <p className="text-center text-gray-400 py-12 text-sm">
              {data.admin ? "Sin productos en inventario" : "Aún no tienes productos asignados. El administrador te los asigna desde Tienda → Productos."}
            </p>
          )}
          {items.map((i) => (
            <Card key={i.id} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{i.nombre}</p>
                    <p className="text-xs text-gray-500">
                      {i.sku || "sin SKU"}{i.codigo_barras ? ` · ▮▮ ${i.codigo_barras}` : ""}
                    </p>
                  </div>
                  {data.admin ? (
                    <div className="text-right text-xs flex-shrink-0">
                      <p><b>{i.bodega}</b> bodega · <b>{i.total_unidades - i.bodega}</b> en técnicos</p>
                      <p className="text-gray-500">costo {formatCLP(i.costo)} · venta {formatCLP(i.precio)} · margen {i.margen_pct}%</p>
                      <p className="text-gray-400">valor: {formatCLP(i.valor_costo)} / {formatCLP(i.valor_venta)}</p>
                    </div>
                  ) : (
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-bold text-[#0FB4B7]">{i.cantidad}</p>
                      <p className="text-[11px] text-gray-400">unidades</p>
                    </div>
                  )}
                </div>
                {data.admin && (i.tecnicos || []).length > 0 && (
                  <p className="mt-1.5 text-[11px] text-gray-500">
                    {i.tecnicos.map((t) => `${t.nombre}: ${t.cantidad}`).join(" · ")}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
